"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ResultBanner,
  PrintButton,
  CopyButton,
  DataReviewedNote,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* Asbestos survey checker — which survey does this job need, and what to do
   with it. A planning aid, not a substitute for a competent survey.

   Verified against quotable sources via the rams-knowledge bank on 2026-06-06:
   - HSG264 (asbestos survey guide): two survey types — a MANAGEMENT survey for
     normal occupation/maintenance, and a REFURBISHMENT & DEMOLITION (R&D)
     survey, which is more intrusive and required before refurbishment or
     demolition.
   - CAR 2012 reg 4 (legislation.gov.uk/uksi/2012/632): the duty to manage
     asbestos in NON-DOMESTIC premises (and the common parts of domestic
     premises) — dutyholder is whoever has the repair/maintenance obligation,
     or control; must assess, record, write and keep a management plan, and
     inform anyone liable to disturb ACMs (and the emergency services).
   - CAR 2012 reg 5: an employer must not do demolition/maintenance/other work
     liable to expose employees to asbestos without a suitable assessment of
     whether asbestos is present — or, if in doubt, assume it is present.
   - HSE Demolition guidance: the client must give the contractor an R&D survey
     by a competent surveyor BEFORE work begins (pre-construction information).
   - CAR 2012 reg 20: air testing and site clearance must be done by bodies
     accredited to ISO 17025 / ISO 17020 (UKAS) — the basis for "use a UKAS-
     accredited surveyor / analyst".

   Cutoff: the UK ban on all asbestos use took full effect at the end of 1999
   (chrysotile banned from 24 Nov 1999, in force 1999). HSE's practical rule is
   that buildings built or refurbished AFTER 2000 are very unlikely to contain
   asbestos in their fabric — but re-used or stored materials and older plant
   can still carry it. */

const HSG264 = "https://www.hse.gov.uk/pubns/books/hsg264.htm";
const CAR_DUTY = "https://www.hse.gov.uk/asbestos/duty.htm";

/* ── Inputs ──────────────────────────────────────────────────────────────── */

type Era = "post2000" | "pre2000" | "unsure";
type Activity = "occupy" | "maintenance" | "refurb" | "demolition";
type Premises = "non-domestic" | "domestic-common" | "domestic-single";
type YN = "yes" | "no" | "unsure";

const ERA_LABEL: Record<Era, string> = {
  post2000: "Built and last refurbished wholly after 2000",
  pre2000: "Built or last refurbished in 2000 or earlier",
  unsure: "Not sure when it was built / refurbished",
};

const ACTIVITY_LABEL: Record<Activity, string> = {
  occupy: "Just occupying / managing the building",
  maintenance: "Maintenance or minor works",
  refurb: "Refurbishment, alteration or strip-out",
  demolition: "Demolition (full or partial)",
};

const PREMISES_LABEL: Record<Premises, string> = {
  "non-domestic": "Non-domestic (commercial, industrial, public)",
  "domestic-common": "Domestic block — common parts (stairs, landings, plant rooms)",
  "domestic-single": "A single private dwelling (someone's own home)",
};

interface Answers {
  era?: Era;
  activity?: Activity;
  premises?: Premises;
  register?: YN; // existing asbestos register / management survey on record
  disturb?: YN; // will the work disturb the building fabric
}

type Survey = "none" | "management" | "rnd";

interface Outcome {
  survey: Survey;
  level: Verdict;
  headline: string;
  reasoning: string;
  /** Duty-to-manage context, where relevant. */
  dutyNote?: string;
}

/* ── Decision logic — conservative ───────────────────────────────────────────
   Post-2000 (wholly) → asbestos in the fabric is very unlikely; no survey
   driven by the fabric, but caveat re-used materials and plant.
   Otherwise (pre-2000 or unknown):
     - refurb / demolition / fabric-disturbing maintenance → R&D survey BEFORE
       work, no exceptions for "quick jobs".
     - occupation / management / non-disturbing maintenance → management survey
       (unless a current one already exists). */

function decide(a: Answers): Outcome {
  const era = a.era!;
  const activity = a.activity!;
  const premises = a.premises!;
  const disturb = a.disturb !== "no"; // unsure → treat as may disturb
  const hasRegister = a.register === "yes";

  const dutyNote =
    premises === "non-domestic"
      ? "Duty to manage (CAR 2012 reg 4): in non-domestic premises someone holds a legal duty to find out whether asbestos is present, record its location and condition, keep a written management plan, and tell anyone liable to disturb it. The dutyholder is whoever has the repair/maintenance obligation, or control of the premises."
      : premises === "domestic-common"
        ? "Duty to manage (CAR 2012 reg 4): the common parts of domestic blocks (stairwells, landings, plant and boiler rooms, lift shafts) count as non-domestic for the duty to manage — the landlord, managing agent or freeholder typically holds it."
        : undefined;

  // ── Post-2000, wholly ─────────────────────────────────────────────────
  if (era === "post2000") {
    return {
      survey: "none",
      level: "low",
      headline: "Asbestos in the fabric is unlikely — but don't assume",
      reasoning:
        "A building constructed and last refurbished wholly after 2000 is very unlikely to contain asbestos in its fabric, because the UK ban on asbestos use took full effect at the end of 1999. So the building's age alone doesn't trigger a survey. Caveat: re-used or salvaged materials, imported products and older plant or equipment can still contain asbestos, and the \"after 2000\" date must be genuinely certain for the whole structure — if any earlier element survives, treat it as pre-2000.",
      dutyNote,
    };
  }

  // ── Pre-2000 or unknown ───────────────────────────────────────────────
  const eraCaveat =
    era === "unsure"
      ? " You weren't sure of the age — an unknown age must be treated as pre-2000 until proven otherwise (CAR 2012 reg 5: if in doubt, assume asbestos is present)."
      : "";

  // Refurbishment / demolition, or fabric-disturbing maintenance → R&D survey.
  if (activity === "demolition" || activity === "refurb" || (activity === "maintenance" && disturb)) {
    const verb = activity === "demolition" ? "demolition" : activity === "refurb" ? "refurbishment" : "fabric-disturbing maintenance";
    return {
      survey: "rnd",
      level: "veryhigh",
      headline: "Refurbishment & demolition (R&D) survey — before any work starts",
      reasoning:
        `Before ${verb} in a pre-2000 building you need a refurbishment & demolition survey. It is more intrusive than a management survey — it goes into voids, ceilings, ducts and structure to find asbestos that will be disturbed. It must be completed BEFORE the work begins; there is no exception for a \"quick job\". A management survey or an existing register is not enough for this work.` +
        eraCaveat,
      dutyNote,
    };
  }

  // Occupation / management / non-disturbing maintenance.
  if (hasRegister) {
    return {
      survey: "management",
      level: "medium",
      headline: "A current management survey may already cover this — verify it",
      reasoning:
        "For occupation, management or maintenance that won't disturb the fabric, a management survey is the right type — and you said one already exists. Check it is current, covers the areas you'll work in, and that the recorded condition still matches reality before relying on it. If the work turns out to disturb the fabric after all, you need an R&D survey instead." +
        eraCaveat,
      dutyNote,
    };
  }

  return {
    survey: "management",
    level: "high",
    headline: "Management survey needed — locate and record the asbestos first",
    reasoning:
      "For occupying, managing or maintaining a pre-2000 building without disturbing the fabric, you need a management survey: it locates asbestos-containing materials that could be disturbed in normal use and records their type and condition, so they can be managed. This underpins the asbestos register and the management plan." +
      eraCaveat,
    dutyNote,
  };
}

/* What to do with the findings — shown on every result (except the post-2000
   "unlikely" case, where it's reframed as the duty-to-manage baseline). */
const FINDINGS_ACTIONS: string[] = [
  "Record it. Put the findings into an asbestos register and (for non-domestic premises) a written management plan — location, type, condition and how each material is managed.",
  "Brief the people who could disturb it. Tell every worker, contractor and the emergency services about asbestos they might encounter, before they start (CAR 2012 reg 4).",
  "Plan removal / work properly. Licensed work goes to an HSE-licensed contractor; some lower-risk work is notifiable non-licensed (NNLW). Every job needs a written plan of work; in demolition or major refurbishment, asbestos is removed first so far as is reasonably practicable.",
  "Use a competent surveyor / analyst. Use a UKAS-accredited body (or a demonstrably competent surveyor); air testing and site clearance after removal must be done by an accredited analyst (ISO 17025 / ISO 17020).",
];

const EMERGENCY_RULE =
  "If you find — or even suspect — asbestos mid-job: STOP work immediately, isolate the area so no one disturbs it further, and get the material tested before going back. Don't carry on, don't clean it up, don't bin it.";

const SURVEY_LABEL: Record<Survey, string> = {
  none: "No fabric-driven survey (verify duty to manage)",
  management: "Management survey",
  rnd: "Refurbishment & demolition (R&D) survey",
};

/* ── Step machine ────────────────────────────────────────────────────────── */

type Step = "era" | "activity" | "premises" | "register" | "disturb" | "result";

export function AsbestosSurveyChecker() {
  const [step, setStep] = useState<Step>("era");
  const [history, setHistory] = useState<Step[]>([]);
  const [a, setA] = useState<Answers>({});
  const tracked = useRef(false);

  const go = (next: Step) => {
    setHistory((h) => [...h, step]);
    setStep(next);
  };
  const back = () => {
    setHistory((h) => {
      if (h.length === 0) return h;
      setStep(h[h.length - 1]);
      return h.slice(0, -1);
    });
  };
  const restart = () => {
    setA({});
    setHistory([]);
    setStep("era");
    tracked.current = false;
  };

  // Route: post-2000 short-circuits straight to a result after premises (so we
  // still capture duty-to-manage context), skipping the register/disturb steps.
  const chooseActivity = (activity: Activity) => {
    setA((s) => ({ ...s, activity }));
    go("premises");
  };

  const choosePremises = (premises: Premises) => {
    setA((s) => ({ ...s, premises }));
    if (a.era === "post2000") return go("result");
    // Demolition / refurb don't need the management-survey-exists or
    // disturbance questions — they always need an R&D survey when pre-2000.
    if (a.activity === "demolition" || a.activity === "refurb") return go("result");
    if (a.activity === "maintenance") return go("disturb");
    // Occupation / management → does a register already exist?
    return go("register");
  };

  const outcome = useMemo(() => (step === "result" ? decide(a) : null), [step, a]);

  useEffect(() => {
    if (outcome && !tracked.current) {
      tracked.current = true;
      trackToolResult("asbestos-survey-checker", { survey: outcome.survey });
    }
  }, [outcome]);

  // Variable-length path; show progress against the longest route (6 steps).
  const STEP_INDEX: Record<Step, number> = {
    era: 1,
    activity: 2,
    premises: 3,
    register: 4,
    disturb: 4,
    result: 5,
  };
  const totalSteps = 5;
  const current = STEP_INDEX[step];

  const showFindings = outcome && outcome.survey !== "none";

  const buildCopyText = () => {
    if (!outcome) return "";
    const L: string[] = [];
    L.push("ASBESTOS SURVEY — DECISION RECORD");
    L.push(new Date().toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }));
    L.push("");
    L.push(`SURVEY NEEDED: ${SURVEY_LABEL[outcome.survey]}`);
    L.push(outcome.headline);
    L.push(outcome.reasoning);
    if (outcome.dutyNote) L.push(`Duty to manage: ${outcome.dutyNote}`);
    L.push("");
    L.push("ANSWERS GIVEN");
    if (a.era) L.push(`- Building age: ${ERA_LABEL[a.era]}`);
    if (a.activity) L.push(`- Planned activity: ${ACTIVITY_LABEL[a.activity]}`);
    if (a.premises) L.push(`- Premises: ${PREMISES_LABEL[a.premises]}`);
    if (a.register) L.push(`- Existing register / management survey: ${cap(a.register)}`);
    if (a.disturb) L.push(`- Work disturbs the fabric: ${cap(a.disturb)}`);
    L.push("");
    if (showFindings) {
      L.push("WHAT TO DO WITH THE FINDINGS");
      FINDINGS_ACTIONS.forEach((n) => L.push(`- ${n}`));
      L.push("");
    }
    L.push("EMERGENCY RULE");
    L.push(`- ${EMERGENCY_RULE}`);
    L.push("");
    L.push("This is a planning aid, not a survey or a substitute for a competent surveyor. Source: HSG264, CAR 2012 (reg 4, 5, 7, 20) and HSE asbestos guidance.");
    return L.join("\n");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
      {/* ── Wizard ─────────────────────────────────────────────────────── */}
      <div className="card shadow-soft p-6 print-hide">
        <div className="flex items-center justify-between">
          <span className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">
            {step === "result" ? "Result" : `Step ${current} of ${totalSteps}`}
          </span>
          {history.length > 0 && (
            <button
              type="button"
              onClick={back}
              className="inline-flex items-center gap-1 text-[0.82rem] font-semibold text-muted hover:text-ink"
            >
              ← Back
            </button>
          )}
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-mist">
          <div className="h-full rounded-full bg-emerald transition-all" style={{ width: `${(current / totalSteps) * 100}%` }} />
        </div>

        <div className="mt-6">
          {step === "era" && (
            <Question title="When was the building (or plant) built and last refurbished?" note="The UK ban on asbestos use took full effect at the end of 1999. Buildings built and refurbished wholly after 2000 are very unlikely to contain asbestos in their fabric.">
              {(Object.keys(ERA_LABEL) as Era[]).map((e) => (
                <ChoiceButton
                  key={e}
                  selected={a.era === e}
                  onClick={() => {
                    setA((s) => ({ ...s, era: e }));
                    go("activity");
                  }}
                >
                  {ERA_LABEL[e]}
                </ChoiceButton>
              ))}
            </Question>
          )}

          {step === "activity" && (
            <Question title="What's the planned activity?">
              {(Object.keys(ACTIVITY_LABEL) as Activity[]).map((ac) => (
                <ChoiceButton key={ac} selected={a.activity === ac} onClick={() => chooseActivity(ac)}>
                  {ACTIVITY_LABEL[ac]}
                </ChoiceButton>
              ))}
            </Question>
          )}

          {step === "premises" && (
            <Question title="What kind of premises is it?" note="The duty to manage asbestos applies to non-domestic premises and the common parts of domestic blocks.">
              {(Object.keys(PREMISES_LABEL) as Premises[]).map((p) => (
                <ChoiceButton key={p} selected={a.premises === p} onClick={() => choosePremises(p)}>
                  {PREMISES_LABEL[p]}
                </ChoiceButton>
              ))}
            </Question>
          )}

          {step === "register" && (
            <Question title="Is there an existing asbestos register or management survey on record?" note="A current one, that covers the areas you'll be working in.">
              <YesNoUnsure
                question="Is there an existing asbestos register or management survey on record?"
                onYes={() => { setA((s) => ({ ...s, register: "yes" })); go("result"); }}
                onNo={() => { setA((s) => ({ ...s, register: "no" })); go("result"); }}
                onUnsure={() => { setA((s) => ({ ...s, register: "unsure" })); go("result"); }}
              />
            </Question>
          )}

          {step === "disturb" && (
            <Question title="Will the maintenance work disturb the building fabric?" note="Drilling, cutting, lifting tiles or ceilings, opening up ducts or voids, removing panels — anything that breaks into the structure.">
              <YesNoUnsure
                question="Will the maintenance work disturb the building fabric?"
                onYes={() => { setA((s) => ({ ...s, disturb: "yes" })); go("result"); }}
                onNo={() => { setA((s) => ({ ...s, disturb: "no" })); go("result"); }}
                onUnsure={() => { setA((s) => ({ ...s, disturb: "unsure" })); go("result"); }}
              />
            </Question>
          )}

          {step === "result" && outcome && (
            <div>
              <ResultBanner level={outcome.level} title={outcome.headline}>
                <span>{outcome.reasoning}</span>
              </ResultBanner>

              {outcome.dutyNote && (
                <div className="mt-4 rounded-lg border border-line bg-mist/60 px-4 py-3 text-[0.88rem] leading-relaxed text-body">
                  <strong className="text-ink">Duty to manage (CAR 2012 reg 4).</strong> {outcome.dutyNote}
                </div>
              )}

              {showFindings && (
                <div className="mt-5 rounded-xl border border-line bg-mist/50 p-4">
                  <div className="text-[0.8rem] font-bold uppercase tracking-wide text-emerald-deep">
                    What to do with the survey findings
                  </div>
                  <ul className="mt-2.5 space-y-2 text-[0.88rem] leading-relaxed text-body">
                    {FINDINGS_ACTIONS.map((n) => (
                      <li key={n} className="flex gap-2.5">
                        <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-deep" />
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-5 rounded-lg border border-risk-veryhigh/40 bg-[#fdecec] px-4 py-3 text-[0.88rem] leading-relaxed text-[#a31616]">
                <strong className="block">If you find suspect material mid-job</strong>
                {EMERGENCY_RULE}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={back}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
                >
                  ← Change last answer
                </button>
                <button
                  type="button"
                  onClick={restart}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
                >
                  Start again
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 rounded-xl border border-line bg-mist/60 px-4 py-3 text-sm text-body">
          This tells you which survey, not what's there. Need to record asbestos controls in your method
          statement?{" "}
          <Link href="/builder/" className="font-semibold text-emerald-deep underline">
            Build a site-specific RAMS draft
          </Link>
          .
        </div>
      </div>

      {/* ── Printable decision record ─────────────────────────────────── */}
      <div className="space-y-4">
        {outcome ? (
          <>
            <div className="print-area card shadow-soft p-6" id="asbestos-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Asbestos survey — decision record
              </div>
              <h2 className="mt-1 text-lg">
                {new Date().toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={outcome.level} title={outcome.headline} />
              </div>

              <div className="mt-4">
                <RecordHeading>What this means</RecordHeading>
                <p className="mt-1.5 text-sm leading-relaxed text-body">{outcome.reasoning}</p>
                {outcome.dutyNote && (
                  <p className="mt-2 text-sm leading-relaxed text-body">{outcome.dutyNote}</p>
                )}
              </div>

              <div className="mt-4">
                <RecordHeading>Answers given</RecordHeading>
                <dl className="mt-1.5 space-y-1.5 text-sm">
                  {a.era && <RecordLine term="Building age">{ERA_LABEL[a.era]}</RecordLine>}
                  {a.activity && <RecordLine term="Planned activity">{ACTIVITY_LABEL[a.activity]}</RecordLine>}
                  {a.premises && <RecordLine term="Premises">{PREMISES_LABEL[a.premises]}</RecordLine>}
                  {a.register && <RecordLine term="Existing register / survey">{cap(a.register)}</RecordLine>}
                  {a.disturb && <RecordLine term="Work disturbs fabric">{cap(a.disturb)}</RecordLine>}
                </dl>
              </div>

              {showFindings && (
                <div className="mt-4">
                  <RecordHeading>What to do with the findings</RecordHeading>
                  <ul className="mt-1.5 list-disc space-y-1 pl-5 text-[0.84rem] leading-relaxed text-body">
                    {FINDINGS_ACTIONS.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4">
                <RecordHeading>If you find suspect material mid-job</RecordHeading>
                <p className="mt-1.5 text-[0.84rem] leading-relaxed text-body">{EMERGENCY_RULE}</p>
              </div>

              <DataReviewedNote date="6 June 2026">
                Guidance follows HSE&apos;s asbestos survey guide HSG264 and the Control of Asbestos
                Regulations 2012 (reg 4 duty to manage, reg 5 identification, reg 7 plans of work, reg 20
                accreditation). This is a planning aid, not a survey or a substitute for a competent surveyor.
              </DataReviewedNote>

              <PrintFooter toolName="asbestos survey checker" />
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <CopyButton tool="asbestos-survey-checker" buildText={buildCopyText} label="Copy as text" />
              <PrintButton tool="asbestos-survey-checker" label="Print / save record as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer the questions to find out whether you need a management survey or a refurbishment &amp;
            demolition survey, who needs it before what, and what to do with the findings — plus a printable
            decision record for the H&amp;S file.
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Helpers (kept consistent with the other checkers) ────────────────────── */

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Question({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xl leading-snug">{title}</h2>
      {note && <p className="mt-2 text-[0.85rem] leading-relaxed text-muted">{note}</p>}
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}

function ChoiceButton({
  children,
  onClick,
  selected,
}: {
  children: React.ReactNode;
  onClick: () => void;
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left text-[0.92rem] font-semibold leading-snug transition-colors ${
        selected
          ? "border-emerald bg-emerald-tint text-emerald-deep"
          : "border-line-2 bg-white text-ink hover:border-ink hover:bg-mist"
      }`}
    >
      <span>{children}</span>
      <span className="shrink-0 text-muted">→</span>
    </button>
  );
}

function YesNoUnsure({
  question,
  onYes,
  onNo,
  onUnsure,
}: {
  question: string;
  onYes: () => void;
  onNo: () => void;
  onUnsure: () => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-3" role="group" aria-label={question}>
      <button type="button" onClick={onYes} className="rounded-xl border border-line-2 bg-white px-4 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-mist">
        Yes
      </button>
      <button type="button" onClick={onNo} className="rounded-xl border border-line-2 bg-white px-4 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-mist">
        No
      </button>
      <button type="button" onClick={onUnsure} className="rounded-xl border border-line-2 bg-white px-4 py-3 text-sm font-semibold text-muted transition-colors hover:border-ink hover:text-ink">
        Not sure
      </button>
    </div>
  );
}

function RecordHeading({ children }: { children: React.ReactNode }) {
  return <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">{children}</div>;
}

function RecordLine({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
      <dt className="shrink-0 font-semibold text-ink sm:w-44">{term}</dt>
      <dd className="text-body">{children}</dd>
    </div>
  );
}
